// src/pages/prodactions/master-data/distribution-mapping/DistributionMapping.jsx
import React, { useState, useEffect, useRef } from "react";
import {
    Typography,
    Box,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
    Alert,
    TablePagination,
    IconButton,
    Stack,
    Snackbar,
    Button,
    TextField,
    Autocomplete,
} from "@mui/material";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import SearchIcon from '@mui/icons-material/Search';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';

import { useTheme } from "@mui/material/styles";
import '../../../../components/css/ded-style.css';
import useAuthCheck from "../../../../hooks/useAuthCheck";
import { useOutletContext } from "react-router-dom";
import { database } from "../../../../firebase";
import { ref, onValue, set, remove, push } from "firebase/database";
import * as XLSX from "xlsx";

// Import formatters dari file utilitas
import {
    formatNumber,
    formatDecimal,
    formatText
} from "../../../../utils/formatters-master";

// Import fungsi formatLastUpdated dari file terpisah
import { formatLastUpdated } from "../../../../utils/formatLastUpdated";

import CustomBreadcrumbs from "../../../../components/universal-features/breadcrumbs";

// Import CustomTableContainer dari komponen terpisah
import CustomTableContainer from "../../../../components/CustomScrollContainer";

// Helper: ubah onValue menjadi Promise
const onValuePromise = (refTarget) => {
    return new Promise((resolve, reject) => {
        onValue(
            refTarget,
            (snapshot) => resolve(snapshot),
            (error) => reject(error),
            { onlyOnce: true }
        );
    });
};

function DistributionMapping() {
    const { user, loading: authLoading } = useAuthCheck();
    const theme = useTheme();
    const { breadcrumbs } = useOutletContext();

    const [dataList, setDataList] = useState([]);
    const [originalDataList, setOriginalDataList] = useState([]);
    const [loadingData, setLoadingData] = useState(true);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const fileInputRef = useRef(null);
    const [importLoading, setImportLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "info",
    });
    const [searchQuery, setSearchQuery] = useState('');
    const [editingRow, setEditingRow] = useState(null);
    const [editFormData, setEditFormData] = useState({});
    const [lastUpdated, setLastUpdated] = useState(null);
    const [showAddForm, setShowAddForm] = useState(false);
    const [newData, setNewData] = useState({
        distribution_mapping_id: '',
        sector: null,
        classification: null,
        productSpecification: null,
        material: null
    });

    // State untuk data referensi (dropdown)
    const [tradeSectorsList, setTradeSectorsList] = useState([]);
    const [classificationCategoriesList, setClassificationCategoriesList] = useState([]);
    const [productSpecificationsList, setProductSpecificationsList] = useState([]);
    const [masterDataList, setMasterDataList] = useState([]);

    // State untuk animasi rotasi icon Plus/X
    const [isAddIconRotated, setIsAddIconRotated] = useState(false);

    // Fetch data referensi untuk dropdown
    useEffect(() => {
        const fetchReferenceData = async () => {
            try {
                // Fetch Trade Sectors
                const tradeSectorsRef = ref(database, "db_productions/tb_trade_sectors");
                const tradeSectorsSnapshot = await onValuePromise(tradeSectorsRef);
                const tradeSectors = tradeSectorsSnapshot.val();
                const tradeSectorsData = [];
                if (tradeSectors) {
                    Object.keys(tradeSectors).forEach(key => {
                        tradeSectorsData.push({
                            id: tradeSectors[key].sector_id || key,
                            name: tradeSectors[key].sector_name || '',
                            firebaseKey: key
                        });
                    });
                }
                tradeSectorsData.sort((a, b) => a.name.localeCompare(b.name));
                setTradeSectorsList(tradeSectorsData);

                // Fetch Classification Categories
                const categoriesRef = ref(database, "db_productions/tb_classification_categories");
                const categoriesSnapshot = await onValuePromise(categoriesRef);
                const categories = categoriesSnapshot.val();
                const categoriesData = [];
                if (categories) {
                    Object.keys(categories).forEach(key => {
                        categoriesData.push({
                            id: categories[key].classification_categories_id || key,
                            name: categories[key].classification_categories_name || '',
                            firebaseKey: key
                        });
                    });
                }
                categoriesData.sort((a, b) => a.name.localeCompare(b.name));
                setClassificationCategoriesList(categoriesData);

                // Fetch Product Specifications
                const specificationsRef = ref(database, "db_productions/tb_product_specifications");
                const specificationsSnapshot = await onValuePromise(specificationsRef);
                const specifications = specificationsSnapshot.val();
                const specificationsData = [];
                if (specifications) {
                    Object.keys(specifications).forEach(key => {
                        specificationsData.push({
                            id: specifications[key].product_specification_id || key,
                            name: specifications[key].product_specification_name || '',
                            firebaseKey: key
                        });
                    });
                }
                specificationsData.sort((a, b) => a.name.localeCompare(b.name));
                setProductSpecificationsList(specificationsData);

                // Fetch Master Data untuk Material Code dan Description
                const masterDataRef = ref(database, "db_productions/tb_master_data");
                const masterDataSnapshot = await onValuePromise(masterDataRef);
                const masterData = masterDataSnapshot.val();
                const masterDataItems = [];
                if (masterData) {
                    Object.keys(masterData).forEach(key => {
                        masterDataItems.push({
                            material_code: masterData[key].material_code || '',
                            description: masterData[key].description || '',
                            firebaseKey: key
                        });
                    });
                }
                masterDataItems.sort((a, b) => a.material_code.localeCompare(b.material_code));
                setMasterDataList(masterDataItems);

            } catch (error) {
                console.error("Error fetching reference data:", error);
            }
        };

        if (user) {
            fetchReferenceData();
        }
    }, [user]);

    // Fetch main data from db_productions/tb_distribution_mapping
    useEffect(() => {
        const fetchData = async () => {
            setLoadingData(true);
            try {
                const mappingRef = ref(database, "db_productions/tb_distribution_mapping");
                const mappingSnapshot = await onValuePromise(mappingRef);
                const mapping = mappingSnapshot.val();
                const loadedData = [];
                let latestUpdateTime = null;
                
                if (mapping) {
                    Object.keys(mapping).forEach(key => {
                        const record = mapping[key];
                        
                        // Cari nama untuk setiap ID
                        const tradeSector = tradeSectorsList.find(ts => ts.id === record.sector_id);
                        const classification = classificationCategoriesList.find(cc => cc.id === record.classification_categories_id);
                        const productSpec = productSpecificationsList.find(ps => ps.id === record.product_specification_id);
                        const masterData = masterDataList.find(md => md.material_code === record.material_code);
                        
                        loadedData.push({ 
                            firebaseKey: key, 
                            distribution_mapping_id: record.distribution_mapping_id || key,
                            sector_id: record.sector_id || '',
                            sector_name: tradeSector?.name || record.sector_id || '',
                            classification_categories_id: record.classification_categories_id || '',
                            classification_categories_name: classification?.name || record.classification_categories_id || '',
                            product_specification_id: record.product_specification_id || '',
                            product_specification_name: productSpec?.name || record.product_specification_id || '',
                            material_code: record.material_code || '',
                            description: masterData?.description || '',
                            last_updated: record.last_updated || null
                        });
                        
                        // Cari waktu update terakhir
                        if (record.last_updated) {
                            const updateTime = new Date(record.last_updated);
                            if (!latestUpdateTime || updateTime > latestUpdateTime) {
                                latestUpdateTime = updateTime;
                            }
                        }
                    });
                    
                    // Set last updated time
                    if (latestUpdateTime) {
                        setLastUpdated(latestUpdateTime);
                    }
                }

                // Sort by distribution_mapping_id
                loadedData.sort((a, b) => a.distribution_mapping_id.localeCompare(b.distribution_mapping_id));
                
                setOriginalDataList(loadedData);
                setDataList(loadedData);

            } catch (error) {
                console.error("Error fetching data:", error);
                setSnackbar({
                    open: true,
                    message: "Gagal memuat data distribution mapping.",
                    severity: "error",
                });
            } finally {
                setLoadingData(false);
            }
        };

        if (user && tradeSectorsList.length > 0 && classificationCategoriesList.length > 0 && 
            productSpecificationsList.length > 0 && masterDataList.length > 0) {
            fetchData();
        }
    }, [user, tradeSectorsList, classificationCategoriesList, productSpecificationsList, masterDataList]);

    // Effect untuk melakukan pencarian saat searchQuery berubah
    useEffect(() => {
        if (searchQuery) {
            const filtered = originalDataList.filter(item => 
                item.sector_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                item.classification_categories_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                item.product_specification_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                item.material_code.toLowerCase().includes(searchQuery.toLowerCase()) ||
                item.description.toLowerCase().includes(searchQuery.toLowerCase())
            );
            setDataList(filtered);
        } else {
            setDataList(originalDataList);
        }
        setPage(0);
    }, [searchQuery, originalDataList]);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const handleImportButtonClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    // Fungsi import Excel
    const handleImportExcel = async (event) => {
        const file = event.target.files[0];
        if (!file) {
            setSnackbar({
                open: true,
                message: "Tidak ada file yang dipilih.",
                severity: "warning",
            });
            return;
        }

        setImportLoading(true);
        setSnackbar({
            open: true,
            message: "Memulai proses import...",
            severity: "info",
        });

        try {
            // Baca file Excel
            const reader = new FileReader();
            
            reader.onload = async (e) => {
                try {
                    const data = new Uint8Array(e.target.result);
                    const workbook = XLSX.read(data, { type: "array" });

                    const sheetName = workbook.SheetNames[0];
                    const worksheet = workbook.Sheets[sheetName];
                    
                    // Path database
                    const dbPath = "db_productions/tb_distribution_mapping";
                    const tbRef = ref(database, dbPath);

                    // Ambil data yang sudah ada
                    const snapshot = await onValuePromise(tbRef);
                    const existingData = snapshot.exists() ? snapshot.val() : {};

                    let importedCount = 0;
                    let updatedCount = 0;
                    let ignoredCount = 0;
                    const errors = [];

                    // Gunakan data dengan default settings
                    const jsonData = XLSX.utils.sheet_to_json(worksheet);
                    const dataToProcess = jsonData;

                    for (const item of dataToProcess) {
                        try {
                            // Cari semua kemungkinan nama kolom
                            let distributionMappingId = "";
                            let sectorId = "";
                            let classificationId = "";
                            let productSpecificationId = "";
                            let materialCode = "";

                            // Cari berdasarkan berbagai kemungkinan nama kolom
                            if (typeof item === 'object') {
                                distributionMappingId = item["Distribution Mapping ID"] || item["distribution_mapping_id"] || 
                                                      item["Kode Mapping"] || item["DISTRIBUTION MAPPING ID"] || "";
                                
                                // Cari sector_id berdasarkan sector_name jika diperlukan
                                const sectorName = item["Trade Sector"] || item["Sektor"] || item["sector_name"] || 
                                                  item["TRADE SECTOR"] || "";
                                if (sectorName) {
                                    const tradeSector = tradeSectorsList.find(ts => 
                                        ts.name.toLowerCase() === sectorName.toString().toLowerCase().trim()
                                    );
                                    sectorId = tradeSector?.id || sectorName.toString().trim();
                                } else {
                                    sectorId = item["Sector ID"] || item["sector_id"] || "";
                                }
                                
                                // Cari classification_id berdasarkan classification_name jika diperlukan
                                const classificationName = item["Classification"] || item["Klasifikasi"] || 
                                                          item["classification_name"] || item["CLASSIFICATION"] || "";
                                if (classificationName) {
                                    const classification = classificationCategoriesList.find(cc => 
                                        cc.name.toLowerCase() === classificationName.toString().toLowerCase().trim()
                                    );
                                    classificationId = classification?.id || classificationName.toString().trim();
                                } else {
                                    classificationId = item["Classification ID"] || item["classification_categories_id"] || "";
                                }
                                
                                // Cari product_specification_id berdasarkan product_specification_name jika diperlukan
                                const productSpecName = item["Product Specification"] || item["Spesifikasi Produk"] || 
                                                       item["product_specification_name"] || item["PRODUCT SPECIFICATION"] || "";
                                if (productSpecName) {
                                    const productSpec = productSpecificationsList.find(ps => 
                                        ps.name.toLowerCase() === productSpecName.toString().toLowerCase().trim()
                                    );
                                    productSpecificationId = productSpec?.id || productSpecName.toString().trim();
                                } else {
                                    productSpecificationId = item["Specification ID"] || item["product_specification_id"] || "";
                                }
                                
                                materialCode = item["Material Code"] || item["material_code"] || 
                                              item["Kode Material"] || item["MATERIAL CODE"] || "";
                            }

                            // Validasi: material_code wajib ada
                            if (!materialCode || materialCode.toString().trim() === "") {
                                console.log("Material Code kosong, diabaikan");
                                ignoredCount++;
                                continue;
                            }

                            // Persiapkan data untuk disimpan
                            const dataToSave = {
                                sector_id: sectorId.toString().trim(),
                                classification_categories_id: classificationId.toString().trim(),
                                product_specification_id: productSpecificationId.toString().trim(),
                                material_code: materialCode.toString().trim(),
                                last_updated: new Date().toISOString()
                            };

                            // Jika distribution_mapping_id diisi, tambahkan ke data
                            if (distributionMappingId && distributionMappingId.toString().trim() !== "") {
                                dataToSave.distribution_mapping_id = distributionMappingId.toString().trim();
                            }

                            // Cek apakah data sudah ada berdasarkan distribution_mapping_id atau kombinasi unik
                            let existingKey = null;
                            
                            if (distributionMappingId && distributionMappingId.toString().trim() !== "") {
                                const mappingIdLower = distributionMappingId.toString().toLowerCase().trim();
                                existingKey = Object.keys(existingData).find(key => 
                                    existingData[key].distribution_mapping_id?.toLowerCase().trim() === mappingIdLower
                                );
                            }

                            if (existingKey) {
                                // Update data yang sudah ada
                                const existingItemRef = ref(database, `${dbPath}/${existingKey}`);
                                await set(existingItemRef, {
                                    ...dataToSave,
                                    distribution_mapping_id: existingData[existingKey].distribution_mapping_id || existingKey
                                });
                                updatedCount++;
                            } else {
                                // Simpan sebagai data baru
                                // Buat key baru menggunakan push
                                const newKeyRef = push(tbRef);
                                const newKey = newKeyRef.key;
                                
                                const newDataToSave = {
                                    ...dataToSave,
                                    distribution_mapping_id: dataToSave.distribution_mapping_id || newKey
                                };
                                
                                await set(newKeyRef, newDataToSave);
                                importedCount++;
                            }

                        } catch (error) {
                            console.error("Error processing row:", error);
                            errors.push(`Error pada baris: ${JSON.stringify(item)} - ${error.message}`);
                            ignoredCount++;
                        }
                    }

                    // Refresh data setelah import
                    await refreshData();

                    // Tampilkan hasil import
                    let message = `Import selesai! ${importedCount} data baru, ${updatedCount} data diperbarui.`;
                    let severity = "success";
                    
                    if (ignoredCount > 0) {
                        message += ` ${ignoredCount} baris diabaikan (error).`;
                        severity = "warning";
                    }
                    
                    if (errors.length > 0) {
                        console.error("Import errors:", errors);
                    }

                    setSnackbar({
                        open: true,
                        message: message,
                        severity: severity,
                    });

                } catch (error) {
                    console.error("Gagal mengimpor Excel:", error);
                    setSnackbar({
                        open: true,
                        message: `Gagal membaca file Excel: ${error.message}`,
                        severity: "error",
                    });
                } finally {
                    setImportLoading(false);
                }
            };

            reader.onerror = (error) => {
                console.error("Error reading file:", error);
                setSnackbar({
                    open: true,
                    message: "Gagal membaca file.",
                    severity: "error",
                });
                setImportLoading(false);
            };

            reader.readAsArrayBuffer(file);

        } catch (error) {
            console.error("Error in import process:", error);
            setSnackbar({
                open: true,
                message: "Terjadi kesalahan saat memproses file.",
                severity: "error",
            });
            setImportLoading(false);
        } finally {
            event.target.value = null;
        }
    };

    // Fungsi untuk refresh data dari Firebase
    const refreshData = async () => {
        try {
            setLoadingData(true);
            const mappingRef = ref(database, "db_productions/tb_distribution_mapping");
            const mappingSnapshot = await onValuePromise(mappingRef);
            const mapping = mappingSnapshot.val();
            const loadedData = [];
            let latestUpdateTime = null;
            
            if (mapping) {
                Object.keys(mapping).forEach(key => {
                    const record = mapping[key];
                    
                    // Cari nama untuk setiap ID
                    const tradeSector = tradeSectorsList.find(ts => ts.id === record.sector_id);
                    const classification = classificationCategoriesList.find(cc => cc.id === record.classification_categories_id);
                    const productSpec = productSpecificationsList.find(ps => ps.id === record.product_specification_id);
                    const masterData = masterDataList.find(md => md.material_code === record.material_code);
                    
                    loadedData.push({ 
                        firebaseKey: key, 
                        distribution_mapping_id: record.distribution_mapping_id || key,
                        sector_id: record.sector_id || '',
                        sector_name: tradeSector?.name || record.sector_id || '',
                        classification_categories_id: record.classification_categories_id || '',
                        classification_categories_name: classification?.name || record.classification_categories_id || '',
                        product_specification_id: record.product_specification_id || '',
                        product_specification_name: productSpec?.name || record.product_specification_id || '',
                        material_code: record.material_code || '',
                        description: masterData?.description || '',
                        last_updated: record.last_updated || null
                    });
                    
                    if (record.last_updated) {
                        const updateTime = new Date(record.last_updated);
                        if (!latestUpdateTime || updateTime > latestUpdateTime) {
                            latestUpdateTime = updateTime;
                        }
                    }
                });
                
                if (latestUpdateTime) {
                    setLastUpdated(latestUpdateTime);
                }
            }

            loadedData.sort((a, b) => a.distribution_mapping_id.localeCompare(b.distribution_mapping_id));
            
            setOriginalDataList(loadedData);
            setDataList(loadedData);
            
        } catch (error) {
            console.error("Error refreshing data:", error);
            throw error;
        } finally {
            setLoadingData(false);
        }
    };

    const handleExport = () => {
        setSnackbar({
            open: true,
            message: "Fungsi Export akan dikembangkan.",
            severity: "info",
        });
    };

    const handlePrint = () => {
        setSnackbar({
            open: true,
            message: "Fungsi Print akan dikembangkan.",
            severity: "info",
        });
    };

    const handleSearchChange = (event) => {
        setSearchQuery(event.target.value);
    };

    const handleEditClick = (row) => {
        // Cari objek lengkap untuk setiap referensi
        const sector = tradeSectorsList.find(ts => ts.id === row.sector_id) || null;
        const classification = classificationCategoriesList.find(cc => cc.id === row.classification_categories_id) || null;
        const productSpec = productSpecificationsList.find(ps => ps.id === row.product_specification_id) || null;
        const material = masterDataList.find(md => md.material_code === row.material_code) || null;

        setEditingRow(row.firebaseKey);
        setEditFormData({
            distribution_mapping_id: row.distribution_mapping_id,
            sector: sector,
            classification: classification,
            productSpecification: productSpec,
            material: material
        });
    };

    const handleEditChange = (field, value) => {
        setEditFormData({
            ...editFormData,
            [field]: value
        });
    };

    const handleSaveEdit = async (firebaseKey) => {
        try {
            // Validasi
            if (!editFormData.material) {
                setSnackbar({
                    open: true,
                    message: "Material Code tidak boleh kosong.",
                    severity: "warning",
                });
                return;
            }

            if (!editFormData.sector) {
                setSnackbar({
                    open: true,
                    message: "Trade Sector harus dipilih.",
                    severity: "warning",
                });
                return;
            }

            if (!editFormData.classification) {
                setSnackbar({
                    open: true,
                    message: "Classification harus dipilih.",
                    severity: "warning",
                });
                return;
            }

            if (!editFormData.productSpecification) {
                setSnackbar({
                    open: true,
                    message: "Product Specification harus dipilih.",
                    severity: "warning",
                });
                return;
            }

            // Update data di Firebase
            const dataRef = ref(database, `db_productions/tb_distribution_mapping/${firebaseKey}`);
            const updatedData = {
                distribution_mapping_id: editFormData.distribution_mapping_id || firebaseKey,
                sector_id: editFormData.sector?.id || '',
                classification_categories_id: editFormData.classification?.id || '',
                product_specification_id: editFormData.productSpecification?.id || '',
                material_code: editFormData.material?.material_code || '',
                last_updated: new Date().toISOString()
            };
            
            await set(dataRef, updatedData);
            
            // Update local state
            const updatedList = dataList.map(item => {
                if (item.firebaseKey === firebaseKey) {
                    return {
                        ...item,
                        ...updatedData,
                        sector_name: editFormData.sector?.name || '',
                        classification_categories_name: editFormData.classification?.name || '',
                        product_specification_name: editFormData.productSpecification?.name || '',
                        description: editFormData.material?.description || '',
                        last_updated: updatedData.last_updated
                    };
                }
                return item;
            });
            
            setDataList(updatedList);
            setOriginalDataList(updatedList);
            setEditingRow(null);
            
            setSnackbar({
                open: true,
                message: "Data berhasil diperbarui.",
                severity: "success",
            });
            
            // Update last updated time
            setLastUpdated(new Date());
            
        } catch (error) {
            console.error("Error updating data:", error);
            setSnackbar({
                open: true,
                message: "Gagal memperbarui data.",
                severity: "error",
            });
        }
    };

    const handleCancelEdit = () => {
        setEditingRow(null);
        setEditFormData({});
    };

    const handleDelete = async (firebaseKey, mappingId) => {
        if (window.confirm(`Apakah Anda yakin ingin menghapus data dengan Distribution Mapping ID: ${mappingId}?`)) {
            try {
                // Hapus data dari Firebase
                const dataRef = ref(database, `db_productions/tb_distribution_mapping/${firebaseKey}`);
                await remove(dataRef);
                
                // Update local state
                const updatedList = dataList.filter(item => item.firebaseKey !== firebaseKey);
                setDataList(updatedList);
                setOriginalDataList(updatedList);
                
                setSnackbar({
                    open: true,
                    message: "Data berhasil dihapus.",
                    severity: "success",
                });
                
                // Update last updated time
                setLastUpdated(new Date());
                
            } catch (error) {
                console.error("Error deleting data:", error);
                setSnackbar({
                    open: true,
                    message: "Gagal menghapus data.",
                    severity: "error",
                });
            }
        }
    };

    const handleAddNew = () => {
        setIsAddIconRotated(!isAddIconRotated);
        setShowAddForm(!showAddForm);
        // Reset form
        if (!showAddForm) {
            setNewData({
                distribution_mapping_id: '',
                sector: null,
                classification: null,
                productSpecification: null,
                material: null
            });
        }
    };

    const handleNewDataChange = (field, value) => {
        setNewData({
            ...newData,
            [field]: value
        });
    };

    const handleSaveNew = async () => {
        // Validasi
        if (!newData.material) {
            setSnackbar({
                open: true,
                message: "Material Code tidak boleh kosong.",
                severity: "warning",
            });
            return;
        }

        if (!newData.sector) {
            setSnackbar({
                open: true,
                message: "Trade Sector harus dipilih.",
                severity: "warning",
            });
            return;
        }

        if (!newData.classification) {
            setSnackbar({
                open: true,
                message: "Classification harus dipilih.",
                severity: "warning",
            });
            return;
        }

        if (!newData.productSpecification) {
            setSnackbar({
                open: true,
                message: "Product Specification harus dipilih.",
                severity: "warning",
            });
            return;
        }

        try {
            // Buat key baru menggunakan push
            const mappingRef = ref(database, "db_productions/tb_distribution_mapping");
            const newKeyRef = push(mappingRef);
            const newKey = newKeyRef.key;
            
            // Persiapkan data untuk disimpan
            const dataToSave = {
                distribution_mapping_id: newData.distribution_mapping_id.trim() || newKey,
                sector_id: newData.sector.id,
                classification_categories_id: newData.classification.id,
                product_specification_id: newData.productSpecification.id,
                material_code: newData.material.material_code,
                last_updated: new Date().toISOString()
            };
            
            await set(newKeyRef, dataToSave);
            
            // Update local state
            const newItem = {
                firebaseKey: newKey,
                ...dataToSave,
                sector_name: newData.sector.name,
                classification_categories_name: newData.classification.name,
                product_specification_name: newData.productSpecification.name,
                description: newData.material.description
            };
            
            const updatedList = [...originalDataList, newItem].sort((a, b) => 
                a.distribution_mapping_id.localeCompare(b.distribution_mapping_id)
            );
            
            setDataList(updatedList);
            setOriginalDataList(updatedList);
            
            // Reset form dan state
            setNewData({
                distribution_mapping_id: '',
                sector: null,
                classification: null,
                productSpecification: null,
                material: null
            });
            setShowAddForm(false);
            setIsAddIconRotated(false);
            
            setSnackbar({
                open: true,
                message: "Data berhasil ditambahkan.",
                severity: "success",
            });
            
            // Update last updated time
            setLastUpdated(new Date());
            
        } catch (error) {
            console.error("Error adding data:", error);
            setSnackbar({
                open: true,
                message: "Gagal menambahkan data.",
                severity: "error",
            });
        }
    };

    const handleCancelNew = () => {
        setShowAddForm(false);
        setIsAddIconRotated(false);
        setNewData({
            distribution_mapping_id: '',
            sector: null,
            classification: null,
            productSpecification: null,
            material: null
        });
    };

    if (authLoading || loadingData) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!user) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">
                    Anda harus login untuk mengakses halaman ini
                </Alert>
            </Box>
        );
    }

    const displayedDataList = dataList;

    return (
        <Box sx={{ pb: 3 }}>
            {/* Breadcrumbs */}
            <CustomBreadcrumbs breadcrumbItems={breadcrumbs} />

            <Paper sx={{ width: '100%', overflow: 'hidden', p: 2 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                    <Typography variant="h6" component="h2">
                        Distribution Mapping
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <TextField
                            size="small"
                            placeholder="Cari..."
                            value={searchQuery}
                            onChange={handleSearchChange}
                            InputProps={{
                                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                            }}
                            sx={{ width: 250 }}
                        />
                        {/* IconButton Plus/X untuk Tambah Data */}
                        <IconButton 
                            color="primary" 
                            size="small" 
                            onClick={handleAddNew}
                            sx={{
                                transition: 'transform 0.3s ease-in-out',
                                transform: isAddIconRotated ? 'rotate(45deg)' : 'rotate(0deg)',
                                backgroundColor: isAddIconRotated ? '#f2f9ff' : 'transparent',
                                '&:hover': {
                                    backgroundColor: isAddIconRotated ? '#f2f9ff' : 'action.hover'
                                }
                            }}
                        >
                            <AddOutlinedIcon />
                        </IconButton>
                        <IconButton color="primary" size="small" onClick={handleImportButtonClick} disabled={importLoading}>
                            {importLoading ? <CircularProgress size={20} /> : <FileUploadOutlinedIcon />}
                        </IconButton>
                        <input
                            type="file"
                            accept=".xlsx, .xls"
                            ref={fileInputRef}
                            style={{ display: "none" }}
                            onChange={handleImportExcel}
                        />
                        <IconButton color="primary" size="small" onClick={handleExport}>
                            <FileDownloadOutlinedIcon />
                        </IconButton>
                        <IconButton color="primary" size="small" onClick={handlePrint}>
                            <PrintOutlinedIcon />
                        </IconButton>
                    </Stack>
                </Stack>

                {/* Form Tambah Data Baru dengan Autocomplete */}
                {showAddForm && (
                    <Paper sx={{ p: 2, mb: 2, bgcolor: '#f2f9ff' }} elevation={0}>
                        <Typography variant="subtitle1" sx={{ mb: 2 }}>
                            Tambah Data Baru
                        </Typography>
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }} flexWrap="wrap">
                            <TextField
                                label="Mapping ID (Opsional)"
                                size="small"
                                value={newData.distribution_mapping_id}
                                onChange={(e) => handleNewDataChange('distribution_mapping_id', e.target.value)}
                                placeholder="Kosongkan untuk isi otomatis"
                                sx={{ minWidth: '200px', flex: 1 }}
                                helperText="Jika dikosongkan akan diisi otomatis"
                            />
                            
                            <Autocomplete
                                size="small"
                                options={tradeSectorsList}
                                getOptionLabel={(option) => option.name || ''}
                                value={newData.sector}
                                onChange={(event, newValue) => handleNewDataChange('sector', newValue)}
                                renderInput={(params) => (
                                    <TextField 
                                        {...params} 
                                        label="Trade Sector" 
                                        required 
                                        helperText="Ketik untuk mencari"
                                    />
                                )}
                                sx={{ minWidth: '200px', flex: 1 }}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                noOptionsText="Tidak ada data"
                            />
                            
                            <Autocomplete
                                size="small"
                                options={classificationCategoriesList}
                                getOptionLabel={(option) => option.name || ''}
                                value={newData.classification}
                                onChange={(event, newValue) => handleNewDataChange('classification', newValue)}
                                renderInput={(params) => (
                                    <TextField 
                                        {...params} 
                                        label="Classification" 
                                        required 
                                        helperText="Ketik untuk mencari"
                                    />
                                )}
                                sx={{ minWidth: '200px', flex: 1 }}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                noOptionsText="Tidak ada data"
                            />
                            
                            <Autocomplete
                                size="small"
                                options={productSpecificationsList}
                                getOptionLabel={(option) => option.name || ''}
                                value={newData.productSpecification}
                                onChange={(event, newValue) => handleNewDataChange('productSpecification', newValue)}
                                renderInput={(params) => (
                                    <TextField 
                                        {...params} 
                                        label="Product Specification" 
                                        required 
                                        helperText="Ketik untuk mencari"
                                    />
                                )}
                                sx={{ minWidth: '200px', flex: 1 }}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                noOptionsText="Tidak ada data"
                            />
                            
                            <Autocomplete
                                size="small"
                                options={masterDataList}
                                getOptionLabel={(option) => `${option.material_code} - ${option.description}`}
                                value={newData.material}
                                onChange={(event, newValue) => handleNewDataChange('material', newValue)}
                                renderInput={(params) => (
                                    <TextField 
                                        {...params} 
                                        label="Material Code" 
                                        required 
                                        helperText="Ketik kode atau deskripsi"
                                    />
                                )}
                                sx={{ minWidth: '200px', flex: 1 }}
                                isOptionEqualToValue={(option, value) => option.material_code === value.material_code}
                                noOptionsText="Tidak ada data"
                            />
                        </Stack>
                        <Stack direction="row" spacing={2} justifyContent="flex-end">
                            <Button variant="outlined" onClick={handleCancelNew}>
                                Batal
                            </Button>
                            <Button variant="contained" onClick={handleSaveNew}>
                                Simpan
                            </Button>
                        </Stack>
                    </Paper>
                )}

                {displayedDataList.length > 0 ? (
                    <>
                        {/* Gunakan CustomTableContainer di sini */}
                        <CustomTableContainer
                            sx={{ 
                                maxHeight: 500,
                                border: '1px solid #e0e0e0',
                                borderRadius: 1
                            }}
                            hoverScrollbar={true}
                            scrollbarHeight="8px"
                            scrollbarWidth="8px"
                            scrollbarThumbColor="#c1c1c1"
                            scrollbarHoverColor="#a8a8a8"
                        >
                            <Table stickyHeader aria-label="distribution mapping table">
                                <TableHead>
                                    <TableRow>
                                        <TableCell className="ded-tch" style={{ width: '50px' }}>No</TableCell>
                                        <TableCell className="ded-tch">Trade Sector</TableCell>
                                        <TableCell className="ded-tch">Classification</TableCell>
                                        <TableCell className="ded-tch">Product Specification</TableCell>
                                        <TableCell className="ded-tch">Material Code</TableCell>
                                        <TableCell className="ded-tch">Description</TableCell>
                                        <TableCell className="ded-tch" style={{ width: '100px' }}>Actions</TableCell>
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {displayedDataList
                                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                        .map((row, index) => (
                                            <TableRow key={row.firebaseKey}>
                                                {editingRow === row.firebaseKey ? (
                                                    <>
                                                        <TableCell>
                                                            {page * rowsPerPage + index + 1}
                                                        </TableCell>
                                                        <TableCell>
                                                            <Autocomplete
                                                                size="small"
                                                                options={tradeSectorsList}
                                                                getOptionLabel={(option) => option.name || ''}
                                                                value={editFormData.sector}
                                                                onChange={(event, newValue) => handleEditChange('sector', newValue)}
                                                                renderInput={(params) => (
                                                                    <TextField 
                                                                        {...params} 
                                                                        required 
                                                                        placeholder="Pilih Trade Sector"
                                                                    />
                                                                )}
                                                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                                                noOptionsText="Tidak ada data"
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            <Autocomplete
                                                                size="small"
                                                                options={classificationCategoriesList}
                                                                getOptionLabel={(option) => option.name || ''}
                                                                value={editFormData.classification}
                                                                onChange={(event, newValue) => handleEditChange('classification', newValue)}
                                                                renderInput={(params) => (
                                                                    <TextField 
                                                                        {...params} 
                                                                        required 
                                                                        placeholder="Pilih Classification"
                                                                    />
                                                                )}
                                                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                                                noOptionsText="Tidak ada data"
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            <Autocomplete
                                                                size="small"
                                                                options={productSpecificationsList}
                                                                getOptionLabel={(option) => option.name || ''}
                                                                value={editFormData.productSpecification}
                                                                onChange={(event, newValue) => handleEditChange('productSpecification', newValue)}
                                                                renderInput={(params) => (
                                                                    <TextField 
                                                                        {...params} 
                                                                        required 
                                                                        placeholder="Pilih Product Specification"
                                                                    />
                                                                )}
                                                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                                                noOptionsText="Tidak ada data"
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            <Autocomplete
                                                                size="small"
                                                                options={masterDataList}
                                                                getOptionLabel={(option) => `${option.material_code} - ${option.description}`}
                                                                value={editFormData.material}
                                                                onChange={(event, newValue) => handleEditChange('material', newValue)}
                                                                renderInput={(params) => (
                                                                    <TextField 
                                                                        {...params} 
                                                                        required 
                                                                        placeholder="Pilih Material Code"
                                                                    />
                                                                )}
                                                                isOptionEqualToValue={(option, value) => option.material_code === value.material_code}
                                                                noOptionsText="Tidak ada data"
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            {editFormData.material?.description || ''}
                                                        </TableCell>
                                                        <TableCell>
                                                            <Stack direction="row" spacing={1}>
                                                                <IconButton 
                                                                    size="small" 
                                                                    color="success"
                                                                    onClick={() => handleSaveEdit(row.firebaseKey)}
                                                                >
                                                                    <CheckOutlinedIcon fontSize="small" />
                                                                </IconButton>
                                                                <IconButton 
                                                                    size="small" 
                                                                    color="error"
                                                                    onClick={handleCancelEdit}
                                                                >
                                                                    <CloseOutlinedIcon fontSize="small" />
                                                                </IconButton>
                                                            </Stack>
                                                        </TableCell>
                                                    </>
                                                ) : (
                                                    <>
                                                        <TableCell>
                                                            {page * rowsPerPage + index + 1}
                                                        </TableCell>
                                                        <TableCell>
                                                            <Box>
                                                                <div style={{ fontWeight: 'bold' }}>
                                                                    {formatText(row.sector_name)}
                                                                </div>
                                                                {row.sector_id && row.sector_id !== row.sector_name && (
                                                                    <div style={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                                                                        ID: {formatText(row.sector_id)}
                                                                    </div>
                                                                )}
                                                            </Box>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Box>
                                                                <div>
                                                                    {formatText(row.classification_categories_name)}
                                                                </div>
                                                                {row.classification_categories_id && row.classification_categories_id !== row.classification_categories_name && (
                                                                    <div style={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                                                                        ID: {formatText(row.classification_categories_id)}
                                                                    </div>
                                                                )}
                                                            </Box>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Box>
                                                                <div>
                                                                    {formatText(row.product_specification_name)}
                                                                </div>
                                                                {row.product_specification_id && row.product_specification_id !== row.product_specification_name && (
                                                                    <div style={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                                                                        ID: {formatText(row.product_specification_id)}
                                                                    </div>
                                                                )}
                                                            </Box>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Box sx={{ fontWeight: 'bold' }}>
                                                                {formatText(row.material_code)}
                                                            </Box>
                                                        </TableCell>
                                                        <TableCell>
                                                            {formatText(row.description)}
                                                        </TableCell>
                                                        <TableCell>
                                                            <Stack direction="row" spacing={1}>
                                                                <IconButton 
                                                                    size="small" 
                                                                    color="primary"
                                                                    onClick={() => handleEditClick(row)}
                                                                >
                                                                    <EditOutlinedIcon fontSize="small" />
                                                                </IconButton>
                                                                <IconButton 
                                                                    size="small" 
                                                                    color="error"
                                                                    onClick={() => handleDelete(row.firebaseKey, row.distribution_mapping_id)}
                                                                >
                                                                    <DeleteOutlinedIcon fontSize="small" />
                                                                </IconButton>
                                                            </Stack>
                                                        </TableCell>
                                                    </>
                                                )}
                                            </TableRow>
                                        ))}
                                </TableBody>
                            </Table>
                        </CustomTableContainer>
                        
                        {/* Container untuk TablePagination dan Last Updated Info */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                            {/* Bagian kiri: Info Data diupdate terakhir */}
                            <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                                Data diupdate terakhir: {formatLastUpdated(lastUpdated)}
                            </Typography>
                            
                            {/* Bagian kanan: TablePagination */}
                            <TablePagination
                                rowsPerPageOptions={[10, 25, 50, 100]}
                                component="div"
                                count={displayedDataList.length}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                sx={{ 
                                    flexShrink: 0,
                                    '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                                        margin: 0
                                    }
                                }}
                            />
                        </Box>
                    </>
                ) : (
                    <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'center', 
                        alignItems: 'center', 
                        minHeight: 200,
                        border: '1px dashed #e0e0e0',
                        borderRadius: 1
                    }}>
                        <Typography variant="body1" color="text.secondary">
                            {searchQuery ? 'Tidak ada data yang sesuai dengan pencarian' : 'Tidak ada data distribution mapping. Klik icon "+" untuk menambahkan atau "Import Excel" untuk mengimpor data.'}
                        </Typography>
                    </Box>
                )}
            </Paper>
            
            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default DistributionMapping;